import React, { FunctionComponent, memo, useMemo } from 'react'
import clsx from 'clsx'
import styles from './ProjectLineDesktop.module.sass'
import {
    EMPTY_PROJECT,
    ProjectProcessStatus,
    ProjectStatus,
    projectStatusToColor
} from '../../../../../responses/projects'
import Button from '../../../../controls/Button/Button'
import { Link } from 'react-router-dom'
import { flow } from 'fp-ts/es6/function'
import { parseDate, toDateString } from '../../../../../utils/date-utils'
import { objectDeclination, taskDeclination } from '../../../../../utils/utils'
import UserPanel from '../../../../data-components/elements/UserElement/UserElement'
import WorksectionIcon from 'files/worksection.svg'
import Menu from '../../../../etc/Menu/Menu'
import { PERMISSIONS } from '../../../../../constants/permissions'
import { push } from 'connected-react-router'
import { urlWithParams } from '../../../../../http/utils'
import { ROUTES } from '../../../../../http/routes'
import { useAnyPermissions, useRootDispatch, useRootSelector } from '../../../../../utils/hooks'
import { projectSelector } from '../../../../../utils/selectors'
import { projectActions } from '../../../../../redux/data/project/project-actions'
import { DoubleCell } from '../DoubleCell'
import UsersSidePanel from '../UsersSidePanel'
import EditIcon from 'files/edit.svg'
import CloseIcon from 'files/close.svg'
import DeleteIcon from 'files/delete.svg'
import ArchiveIcon from 'files/archive/archive.svg'
import FolderErrorIcon from 'files/folder-error.svg'
import UnarchiveIcon from 'files/archive/unarchive.svg'
import SideMark from '../../../../etc/SideMark/SideMark'
import { Classes, Colors } from '../../../../../styles'
import { archiveActions } from '../../../../../redux/pages/archive'
import { enumIs } from '../../../../../utils/enum-utils'
import Spinner from '../../../../etc/Spinner/Spinner'
import Tooltip from '../../../../etc/Tooltip'
import { ProjectLineProps } from './props'

const ProjectLineDesktop: FunctionComponent<ProjectLineProps> = props => {
    const dispatch = useRootDispatch()
    const url = useMemo(() => {
        return urlWithParams({projectId: props.projectId})(ROUTES.PROJECT_DETAIL)
    }, [props.projectId])
    const project = useRootSelector(projectSelector(props.projectId)) ?? EMPTY_PROJECT

    const canEdit = useAnyPermissions([PERMISSIONS.PROJECT.EDIT])
    const canCancel = useAnyPermissions([PERMISSIONS.PROJECT.CANCEL])
    const canArchive = useAnyPermissions([PERMISSIONS.PROJECT.ARCHIVE])
    const canDelete = useAnyPermissions([PERMISSIONS.PROJECT.DELETE])

    return <div
        className={clsx(styles.project, {[styles.minimal]: props.minimal})}
        style={{background: project.status_id === ProjectStatus.NEW ? Colors.lOrange : undefined}}
    >
        <div>
            <SideMark color={project.dir_exists === 0 || project.has_client === 0 ?
                Colors.red:
                projectStatusToColor(project.status_id)
            }/>
            {
                project.dir_exists === 0 || project.has_client === 0 ?
                    <Tooltip title={project.has_client === 0 ? 'Некорректный клиент. Возможно, он был удален или отредактирован.' : 'Папка проекта отсутствует на диске'}>
                        <FolderErrorIcon/>
                    </Tooltip> :
                    project.process_status_id === ProjectProcessStatus.COMPLETE ?
                        props.control?.(project):
                        <Tooltip title={project.process_status_name}><Spinner/></Tooltip>


            }
        </div>
        <Link className={Classes.justifyCenter} to={url}>
            <DoubleCell
                top={project.code}
                bottom={flow(parseDate, toDateString)(project.opened_at)}
            />
        </Link>
        <Link to={url}>
            <DoubleCell
                top={project.brand.title}
                bottom={project.client?.short_title}
            />
        </Link>
        <Link to={url}>
            <DoubleCell
                top={project.short_name}
                bottom={`${project.network?.name || ''} ${project.countries.map(item => item.code).join(',')}`}
            />
        </Link>
        {
            props.minimal ? props.children : <>
                <Link to={url}>
                    <DoubleCell
                        top={objectDeclination(project.work_objects_count)}
                        bottom={taskDeclination(project.tasks_count)}
                    />
                </Link>
                <Link to={url}>
                    <UserPanel user={project.manager}/>
                </Link>
                <div>
                    <UsersSidePanel
                        users={project.users ?? []}
                        onClick={() => props.onUsersClick?.(project.users ?? [])}
                    />
                </div>
                <div className={styles.center}>
                    {
                        !props.archiveMode && project.worksection && <a href={project.worksection}>
                            <Button><WorksectionIcon/></Button>
                        </a>
                    }
                    {
                        project.status_id === ProjectStatus.RESERVED && <div className={styles.strimer}>
                            <span className={Classes.f13b}>Номер не задан</span>
                            <span className={Classes.f11}>Стример</span>
                        </div>
                    }
                </div>
                <div>
                    {
                        (project.status_id !== ProjectStatus.RESERVED && project.dir_exists !== 0) && <Menu items={[
                            {
                                icon: <EditIcon/>,
                                text: 'Редактировать',
                                hide: !canEdit || props.archiveMode,
                                onClick: () => dispatch(push(urlWithParams({projectId: project.id})(ROUTES.PROJECTS_EDIT))),
                            },
                            {
                                icon: <CloseIcon/>,
                                text: 'Отменить',
                                hide: !canCancel ||
                                    props.archiveMode ||
                                    [ProjectStatus.CANCELED, ProjectStatus.CLOSED].includes(project.status_id) ||
                                    project.work_objects_count === 0 || !project.work_type,
                                onClick: () => dispatch(projectActions.startCancel(project)),
                            },
                            {
                                icon: <ArchiveIcon/>,
                                text: 'Архивировать',
                                hide: !props.archiveMode || !enumIs<ProjectStatus>()([ProjectStatus.TO_ARCHIVE, ProjectStatus.CLOSED])(project.status_id) || !canArchive,
                                onClick: () => dispatch(archiveActions.startArchiving([project.id])),
                            },
                            {
                                icon: <UnarchiveIcon/>,
                                text: 'Разархивировать',
                                hide: !props.archiveMode || project.imported || project.status_id !== ProjectStatus.ARCHIVED || !canArchive,
                                onClick: () => dispatch(archiveActions.startUnarchiving(project)),
                            },
                            {
                                icon: <DeleteIcon/>,
                                text: 'Удалить',
                                hide: props.archiveMode || !canDelete,
                                onClick: () => dispatch(projectActions.startDelete(project)),
                            }
                        ]}/>
                    }
                </div>
            </>
        }
    </div>
}

export default memo(ProjectLineDesktop)